import type { TKey } from "@/lib/i18n";
import type { Tone } from "@fox1ck-org/ui";

/** A status colour: the package's tones without the neutral and the brand ones. */
type StatusTone = Exclude<Tone, "neutral" | "primary">;

/**
 * The edge's traffic classes (internal/edgeevents classify; decision 01:
 * «Клики» = human). `bot` is a visit recorded before classes existed, read
 * from bot_flag. An unknown class keeps its own word rather than vanishing.
 */
const CLASS_LABEL: Record<string, TKey> = {
  human: "tc.human",
  verified_bot: "tc.verified_bot",
  meta_crawler: "tc.meta_crawler",
  listed_ip: "tc.listed_ip",
  crawler: "tc.crawler",
  no_ua: "tc.no_ua",
  datacenter: "tc.datacenter",
  bot_score: "tc.bot_score",
  bot: "tc.bot",
};

/** Every class that is not a person — the «Боты» side of decision 01. */
export const BOT_CLASSES = Object.keys(CLASS_LABEL).filter((c) => c !== "human");

export function trafficClassLabel(t: (k: TKey) => string, cls: string): string {
  const key = CLASS_LABEL[cls];
  return key ? t(key) : cls || "—";
}

export function trafficClassTone(cls: string): StatusTone | undefined {
  if (cls === "human") return "ok";
  if (cls === "verified_bot" || cls === "meta_crawler") return undefined;
  return "warn";
}

/** «AS32934» from whatever the record holds ("32934", "AS32934", 32934); "" when none. Pure. */
export function asnLabel(asn: string | number | undefined): string {
  const raw = String(asn ?? "").trim().replace(/^AS/i, "");
  return raw && raw !== "0" ? `AS${raw}` : "";
}

/**
 * The visitor's network in one phrase — «AS32934 Facebook»: the autonomous
 * system and who runs it, either half alone when that is all there is. Pure.
 */
export function networkLabel(asn: string | number | undefined, org: string | undefined): string {
  return [asnLabel(asn), (org ?? "").trim()].filter(Boolean).join(" ");
}
